import { computed, inject, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import {
  clearAnalyticsContext,
  enrichAnalyticsContext,
  getAnalyticsContextSnapshot,
  loadAnalyticsContext,
  sanitizeContext,
  syncAnalyticsBaseContext,
} from './analytics-context.manager'
import { resolveUserProfile } from './resolve-user-profile.util'

const AMPLITUDE_ANONYMOUS_DEVICE_KEY = 'amplitude_anonymous_device_id'

/**
 * Analytics composable for tracking user events and identifying users
 * Integrates with Amplitude Analytics + Session Replay (Autocapture enabled)
 *
 * Jira: EE-131 - Implement Autocapture Amplitude for dss+ 360 Transformation
 *
 * @example
 * const { trackEvent, identifyUser, resetUser, loadContext, enrichContext } = useAnalytics()
 *
 * // Auto-tracked (no code needed):
 * // - Button clicks
 * // - Page views
 * // - Form interactions
 * // - File downloads
 *
 * // Manual tracking (optional - for custom properties):
 * loadContext()
 * enrichContext({ site_id: 'site_123' }, { scope: 'module' })
 * trackEvent('report_exported', { report_type: 'incidents', format: 'pdf' })
 *
 * // User identification (login):
 * identifyUser()
 *
 * // Reset (logout):
 * resetUser()
 */
export function useAnalytics() {
  const amplitude = inject('amplitude', null)
  const authStore = useAuthStore()
  const router = useRouter()
  const isInitialized = ref(false)
  const lastIdentifySignature = ref('')

  function captureAnonymousDeviceId() {
    if (!amplitude || typeof amplitude.getDeviceId !== 'function')
      return null

    const deviceIdAnonymous = amplitude.getDeviceId()
    if (deviceIdAnonymous)
      sessionStorage.setItem(AMPLITUDE_ANONYMOUS_DEVICE_KEY, deviceIdAnonymous)

    return deviceIdAnonymous
  }

  function trackLoginScreenViewed() {
    if (!amplitude)
      return

    captureAnonymousDeviceId()
    amplitude.track('Tela de Login Visualizada')
  }

  // Auto-identify user when authenticated
  onMounted(() => {
    syncAnalyticsBaseContext({
      route: router.currentRoute.value,
      authStore,
    })

    if (amplitude && authStore.isAuthenticated && authStore.userData) {
      identifyUser()
    }
  })

  /**
   * Track custom event with enriched properties
   * Note: With Autocapture enabled, most events are tracked automatically.
   * Use this for custom events with specific business context.
   *
   * @param {string} eventName - Event name (use snake_case convention)
   * @param {object} properties - Event properties
   * @param {object} contextOverride - Optional context override for this event only
   */
  function trackEvent(eventName, properties = {}, contextOverride = {}) {
    if (!amplitude)
      return

    try {
      syncAnalyticsBaseContext({
        route: router.currentRoute.value,
        authStore,
      })

      const enrichedProperties = {
        ...properties,
        ...getAnalyticsContextSnapshot(),
        ...sanitizeContext(contextOverride),
      }

      amplitude.track(eventName, enrichedProperties)
    }
    catch (error) {
      console.warn('[Analytics] trackEvent failed:', error)
    }
  }

  /**
   * Identify user and set user properties
   * Automatically called when user authenticates
   *
   * @param {string | null} b2cIdOverride - Optional b2cId override for Amplitude user identification.
   */
  function identifyUser(b2cIdOverride = null) {
    if (!amplitude)
      return

    const user = authStore.userData
    const effectiveB2cId = b2cIdOverride || user?.b2cId

    if (!effectiveB2cId) {
      console.warn('[Analytics] Cannot identify user: no b2cId available')
      return
    }

    const profile = resolveUserProfile({ roles: authStore.userRoles })

    const identifySignature = JSON.stringify({
      b2c_id: effectiveB2cId,
      profile,
    })

    if (isInitialized.value && lastIdentifySignature.value === identifySignature)
      return

    const deviceIdAnonymous = sessionStorage.getItem(AMPLITUDE_ANONYMOUS_DEVICE_KEY)
    if (deviceIdAnonymous) {
      amplitude.setDeviceId(deviceIdAnonymous)
      sessionStorage.removeItem(AMPLITUDE_ANONYMOUS_DEVICE_KEY)
    }

    amplitude.setUserId(effectiveB2cId)

    const identifyEvent = new amplitude.Identify()
    identifyEvent.set('Profile', profile)
    amplitude.identify(identifyEvent)
    lastIdentifySignature.value = identifySignature
    isInitialized.value = true
  }

  /**
   * Reset user session on logout
   * Clears user ID and session data
   */
  function resetUser() {
    if (!amplitude)
      return

    amplitude.reset()
    clearAnalyticsContext('all')
    lastIdentifySignature.value = ''
    isInitialized.value = false
  }

  return {
    captureAnonymousDeviceId,
    trackLoginScreenViewed,
    trackEvent,
    loadContext: loadAnalyticsContext,
    enrichContext: enrichAnalyticsContext,
    clearContext: clearAnalyticsContext,
    getContextSnapshot: getAnalyticsContextSnapshot,
    identifyUser,
    resetUser,
    isInitialized: computed(() => isInitialized.value),
    isEnabled: computed(() => !!amplitude),
  }
}
